import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, LinearProgress, IconButton, Alert,
  Divider, FormControl, InputLabel, Select, InputAdornment,
} from '@mui/material';
import {
  MonetizationOn, Add, Edit, Delete, Close, CheckCircle, Warning,
  TrendingUp, People, PlayArrow, Lock, Undo,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

// ── API ───────────────────────────────────────────────────────────────────────
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || 'Error');
    return d;
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────
const CYCLE_TYPE_LABELS: Record<string, string> = {
  annual: 'Annual', mid_year: 'Mid-Year', promotion: 'Promotion', ad_hoc: 'Ad Hoc',
};
const CYCLE_STATUS_COLORS: Record<string, any> = {
  draft: 'default', active: 'success', closed: 'warning', archived: 'default',
};
const REVIEW_STATUS_COLORS: Record<string, any> = {
  pending: 'default', submitted: 'primary', approved: 'success',
  rejected: 'error', finalised: 'secondary',
};
const REVIEW_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', submitted: 'Submitted', approved: 'Approved',
  rejected: 'Rejected', finalised: 'Finalised',
};

function fmtCurrency(val: number | null | undefined, currency = 'HKD') {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-HK', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(val));
}
function fmtPct(val: number | null | undefined) {
  if (val == null) return '—';
  const v = Number(val);
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface Cycle {
  id: string; name: string; type: string; status: string;
  startDate: string; endDate: string; effectiveDate: string;
  budgetAmount: number; currency: string; budgetPercent: number; guidelines: string;
}
interface Review {
  id: string; cycleId: string; employeeId: string; status: string;
  currentSalary: number; recommendedSalary: number; increasePercent: number;
  bonusAmount: number; managerJustification: string;
  approvedSalary: number; approvedBonus: number; hrNotes: string; currency: string;
  employee?: { firstName: string; lastName: string };
  submittedBy?: { firstName: string; lastName: string };
  approvedBy?: { firstName: string; lastName: string };
}
interface CycleStats {
  total: number; byStatus: Record<string, number>;
  totalCurrentPayroll: number; totalRecommendedPayroll: number;
  totalApprovedPayroll: number; totalBonuses: number;
  avgIncreasePercent: number; budgetImpact: number;
}
interface DeptRow { department: string; count: number; avgIncrease: number; totalIncrease: number; totalBonus: number; }

// ── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color, sub }: any) {
  return (
    <Card sx={{ p: 0 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
        <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CompensationPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<Cycle | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<CycleStats | null>(null);
  const [deptBreakdown, setDeptBreakdown] = useState<DeptRow[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  // Dialogs
  const [cycleDialog, setCycleDialog] = useState<Partial<Cycle> | null>(null);
  const [addEmployeeDialog, setAddEmployeeDialog] = useState(false);
  const [addEmpForm, setAddEmpForm] = useState({ employeeId: '', currentSalary: '', currency: 'HKD' });
  const [reviewDialog, setReviewDialog] = useState<Review | null>(null);
  const [approveDialog, setApproveDialog] = useState<Review | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{ id: string; hrNotes: string } | null>(null);

  const loadCycles = useCallback(async () => {
    try { setCycles(await apiFetch('/api/compensation/cycles')); } catch (e: any) { setError(e.message); }
  }, []);

  const loadCycleDetails = useCallback(async (cycle: Cycle) => {
    setLoading(true);
    try {
      const [r, s, d] = await Promise.all([
        apiFetch(`/api/compensation/cycles/${cycle.id}/reviews`),
        apiFetch(`/api/compensation/cycles/${cycle.id}/stats`),
        apiFetch(`/api/compensation/cycles/${cycle.id}/dept-breakdown`),
      ]);
      setReviews(r);
      setStats(s);
      setDeptBreakdown(d);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadCycles(); }, [loadCycles]);

  useEffect(() => {
    if (selectedCycle) loadCycleDetails(selectedCycle);
  }, [selectedCycle, loadCycleDetails]);

  useEffect(() => {
    if (isHR && employees.length === 0) {
      apiFetch('/api/employees?limit=500').then((r) => setEmployees(r.data || r || [])).catch(() => {});
    }
  }, [isHR, employees.length]);

  // ── Cycle actions ────────────────────────────────────────────────────────────
  const saveCycle = async () => {
    if (!cycleDialog) return;
    try {
      if (cycleDialog.id) {
        await apiFetch(`/api/compensation/cycles/${cycleDialog.id}`, { method: 'PATCH', body: JSON.stringify(cycleDialog) });
      } else {
        await apiFetch('/api/compensation/cycles', { method: 'POST', body: JSON.stringify(cycleDialog) });
      }
      setCycleDialog(null);
      loadCycles();
    } catch (e: any) { setError(e.message); }
  };

  const activateCycle = async (id: string) => {
    try { await apiFetch(`/api/compensation/cycles/${id}/activate`, { method: 'POST' }); loadCycles(); if (selectedCycle?.id === id) loadCycleDetails({ ...selectedCycle, status: 'active' }); }
    catch (e: any) { setError(e.message); }
  };

  const closeCycle = async (id: string) => {
    if (!window.confirm('Close this cycle? No more changes can be submitted.')) return;
    try { await apiFetch(`/api/compensation/cycles/${id}/close`, { method: 'POST' }); loadCycles(); }
    catch (e: any) { setError(e.message); }
  };

  // ── Review actions ───────────────────────────────────────────────────────────
  const addEmployee = async () => {
    if (!selectedCycle) return;
    try {
      await apiFetch(`/api/compensation/cycles/${selectedCycle.id}/reviewees`, {
        method: 'POST',
        body: JSON.stringify({ ...addEmpForm, currentSalary: Number(addEmpForm.currentSalary) || undefined }),
      });
      setAddEmployeeDialog(false);
      setAddEmpForm({ employeeId: '', currentSalary: '', currency: 'HKD' });
      loadCycleDetails(selectedCycle);
    } catch (e: any) { setError(e.message); }
  };

  const submitRecommendation = async () => {
    if (!reviewDialog) return;
    try {
      await apiFetch(`/api/compensation/reviews/${reviewDialog.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({
          recommendedSalary: Number(reviewDialog.recommendedSalary),
          bonusAmount: Number(reviewDialog.bonusAmount) || undefined,
          managerJustification: reviewDialog.managerJustification,
        }),
      });
      setReviewDialog(null);
      if (selectedCycle) loadCycleDetails(selectedCycle);
    } catch (e: any) { setError(e.message); }
  };

  const approveReview = async () => {
    if (!approveDialog) return;
    try {
      await apiFetch(`/api/compensation/reviews/${approveDialog.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({
          approvedSalary: Number(approveDialog.approvedSalary) || undefined,
          approvedBonus: Number(approveDialog.approvedBonus) || undefined,
          hrNotes: approveDialog.hrNotes,
        }),
      });
      setApproveDialog(null);
      if (selectedCycle) loadCycleDetails(selectedCycle);
    } catch (e: any) { setError(e.message); }
  };

  const rejectReview = async () => {
    if (!rejectDialog) return;
    try {
      await apiFetch(`/api/compensation/reviews/${rejectDialog.id}/reject`, {
        method: 'POST', body: JSON.stringify({ hrNotes: rejectDialog.hrNotes }),
      });
      setRejectDialog(null);
      if (selectedCycle) loadCycleDetails(selectedCycle);
    } catch (e: any) { setError(e.message); }
  };

  const finaliseReview = async (id: string) => {
    try {
      await apiFetch(`/api/compensation/reviews/${id}/finalise`, { method: 'POST' });
      if (selectedCycle) loadCycleDetails(selectedCycle);
    } catch (e: any) { setError(e.message); }
  };

  const removeReviewee = async (id: string) => {
    if (!window.confirm('Remove this employee from the cycle?')) return;
    try {
      await apiFetch(`/api/compensation/reviews/${id}`, { method: 'DELETE' });
      if (selectedCycle) loadCycleDetails(selectedCycle);
    } catch (e: any) { setError(e.message); }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonetizationOn sx={{ fontSize: 32, color: '#2e7d32' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Compensation Review</Typography>
            <Typography variant="body2" color="text.secondary">Salary review cycles, manager recommendations, and HR approvals</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#2e7d32' }}
            onClick={() => setCycleDialog({ type: 'annual', status: 'draft', currency: 'HKD' })}>
            New Cycle
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Left: Cycle list */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Review Cycles</Typography>
          {cycles.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <MonetizationOn sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
              <Typography>No cycles yet.</Typography>
              {isHR && <Button size="small" sx={{ mt: 1 }} onClick={() => setCycleDialog({ type: 'annual', currency: 'HKD' })}>Create First Cycle</Button>}
            </Paper>
          ) : (
            cycles.map((c) => (
              <Card key={c.id} sx={{
                mb: 1.5, cursor: 'pointer',
                border: selectedCycle?.id === c.id ? '2px solid #2e7d32' : '1px solid #e0e0e0',
                '&:hover': { borderColor: '#2e7d32' },
              }} onClick={() => setSelectedCycle(c)}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography fontWeight={700} variant="body1">{c.name}</Typography>
                    <Chip label={c.status} size="small" color={CYCLE_STATUS_COLORS[c.status] ?? 'default'} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {CYCLE_TYPE_LABELS[c.type]} · {c.startDate} → {c.endDate}
                  </Typography>
                  {c.budgetAmount && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Budget: {fmtCurrency(c.budgetAmount, c.currency)}
                    </Typography>
                  )}
                  {isHR && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                      {c.status === 'draft' && (
                        <>
                          <Button size="small" startIcon={<Edit />} onClick={(e) => { e.stopPropagation(); setCycleDialog({ ...c }); }}>Edit</Button>
                          <Button size="small" color="success" startIcon={<PlayArrow />} onClick={(e) => { e.stopPropagation(); activateCycle(c.id); }}>Activate</Button>
                        </>
                      )}
                      {c.status === 'active' && (
                        <Button size="small" color="warning" startIcon={<Lock />} onClick={(e) => { e.stopPropagation(); closeCycle(c.id); }}>Close</Button>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Grid>

        {/* Right: Cycle detail */}
        <Grid size={{ xs: 12, md: 8 }}>
          {!selectedCycle ? (
            <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>Select a review cycle to see details.</Typography>
            </Paper>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>{selectedCycle.name}</Typography>
                {isHR && selectedCycle.status === 'active' && (
                  <Button size="small" variant="outlined" startIcon={<Add />}
                    onClick={() => setAddEmployeeDialog(true)}>
                    Add Employee
                  </Button>
                )}
              </Box>

              {/* Stats row */}
              {stats && (
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <StatCard label="In Cycle" value={stats.total} icon={<People />} color="#1565c0" />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <StatCard label="Submitted" value={stats.byStatus?.submitted ?? 0} icon={<TrendingUp />} color="#f57c00" />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <StatCard label="Approved" value={stats.byStatus?.approved ?? 0} icon={<CheckCircle />} color="#2e7d32" />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <StatCard
                      label="Avg Increase"
                      value={fmtPct(stats.avgIncreasePercent)}
                      icon={<MonetizationOn />}
                      color="#7b1fa2"
                      sub={`Budget impact: ${fmtCurrency(stats.budgetImpact, selectedCycle.currency)}`}
                    />
                  </Grid>
                </Grid>
              )}

              <Paper sx={{ mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                  <Tab label="Reviews" />
                  <Tab label="By Department" />
                </Tabs>
              </Paper>

              {loading && <LinearProgress sx={{ mb: 1 }} />}

              {/* Reviews table */}
              {tab === 0 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><b>Employee</b></TableCell>
                        <TableCell><b>Current</b></TableCell>
                        <TableCell><b>Recommended</b></TableCell>
                        <TableCell><b>Increase</b></TableCell>
                        <TableCell><b>Bonus</b></TableCell>
                        <TableCell><b>Status</b></TableCell>
                        <TableCell><b>Actions</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reviews.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            No employees in this cycle yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        reviews.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {r.employee ? `${r.employee.firstName} ${r.employee.lastName}` : r.employeeId}
                              </Typography>
                            </TableCell>
                            <TableCell>{fmtCurrency(r.currentSalary, r.currency)}</TableCell>
                            <TableCell>{fmtCurrency(r.approvedSalary ?? r.recommendedSalary, r.currency)}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2" fontWeight={700}
                                color={Number(r.increasePercent) > 0 ? 'success.main' : 'text.secondary'}>
                                {fmtPct(r.increasePercent)}
                              </Typography>
                            </TableCell>
                            <TableCell>{fmtCurrency(r.approvedBonus ?? r.bonusAmount, r.currency)}</TableCell>
                            <TableCell>
                              <Chip
                                label={REVIEW_STATUS_LABELS[r.status]}
                                size="small"
                                color={REVIEW_STATUS_COLORS[r.status] ?? 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {/* Manager: submit recommendation */}
                              {['pending', 'rejected'].includes(r.status) && selectedCycle.status === 'active' && (
                                <Tooltip title="Submit recommendation">
                                  <IconButton size="small" color="primary"
                                    onClick={() => setReviewDialog({ ...r })}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {/* HR: approve */}
                              {r.status === 'submitted' && isHR && (
                                <>
                                  <Tooltip title="Approve">
                                    <IconButton size="small" color="success"
                                      onClick={() => setApproveDialog({
                                        ...r,
                                        approvedSalary: r.recommendedSalary,
                                        approvedBonus: r.bonusAmount,
                                      })}>
                                      <CheckCircle fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton size="small" color="error"
                                      onClick={() => setRejectDialog({ id: r.id, hrNotes: '' })}>
                                      <Undo fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {/* HR: finalise */}
                              {r.status === 'approved' && isHR && (
                                <Tooltip title="Finalise (mark changes applied)">
                                  <IconButton size="small" color="secondary"
                                    onClick={() => finaliseReview(r.id)}>
                                    <Lock fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {/* HR: remove */}
                              {isHR && r.status === 'pending' && (
                                <Tooltip title="Remove from cycle">
                                  <IconButton size="small" color="error"
                                    onClick={() => removeReviewee(r.id)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Dept breakdown */}
              {tab === 1 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><b>Department</b></TableCell>
                        <TableCell><b>Employees</b></TableCell>
                        <TableCell><b>Avg Increase</b></TableCell>
                        <TableCell><b>Total Salary Increase</b></TableCell>
                        <TableCell><b>Total Bonus</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deptBreakdown.map((d) => (
                        <TableRow key={d.department} hover>
                          <TableCell><Typography fontWeight={600}>{d.department}</Typography></TableCell>
                          <TableCell>{d.count}</TableCell>
                          <TableCell>
                            <Typography color={d.avgIncrease > 0 ? 'success.main' : 'text.secondary'} fontWeight={700}>
                              {fmtPct(d.avgIncrease)}
                            </Typography>
                          </TableCell>
                          <TableCell>{fmtCurrency(d.totalIncrease, selectedCycle.currency)}</TableCell>
                          <TableCell>{fmtCurrency(d.totalBonus, selectedCycle.currency)}</TableCell>
                        </TableRow>
                      ))}
                      {deptBreakdown.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                            No data yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* ── CYCLE DIALOG ── */}
      <Dialog open={!!cycleDialog} onClose={() => setCycleDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {cycleDialog?.id ? 'Edit Cycle' : 'New Review Cycle'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setCycleDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Cycle Name" fullWidth required
              placeholder="e.g. 2026 Annual Salary Review"
              value={cycleDialog?.name ?? ''}
              onChange={(e) => setCycleDialog((p) => ({ ...p, name: e.target.value }))} />
            <TextField select label="Type" fullWidth
              value={cycleDialog?.type ?? 'annual'}
              onChange={(e) => setCycleDialog((p) => ({ ...p, type: e.target.value }))}>
              {Object.entries(CYCLE_TYPE_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={cycleDialog?.startDate ?? ''}
                onChange={(e) => setCycleDialog((p) => ({ ...p, startDate: e.target.value }))} />
              <TextField label="End Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={cycleDialog?.endDate ?? ''}
                onChange={(e) => setCycleDialog((p) => ({ ...p, endDate: e.target.value }))} />
            </Box>
            <TextField label="Effective Date (when changes apply)" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={cycleDialog?.effectiveDate ?? ''}
              onChange={(e) => setCycleDialog((p) => ({ ...p, effectiveDate: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Budget Amount" type="number" fullWidth
                value={cycleDialog?.budgetAmount ?? ''}
                InputProps={{ startAdornment: <InputAdornment position="start">{cycleDialog?.currency ?? 'HKD'}</InputAdornment> }}
                onChange={(e) => setCycleDialog((p) => ({ ...p, budgetAmount: Number(e.target.value) }))} />
              <TextField label="Budget %" type="number" fullWidth
                value={cycleDialog?.budgetPercent ?? ''}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                onChange={(e) => setCycleDialog((p) => ({ ...p, budgetPercent: Number(e.target.value) }))} />
            </Box>
            <TextField label="Guidelines for managers" fullWidth multiline rows={3}
              value={cycleDialog?.guidelines ?? ''}
              onChange={(e) => setCycleDialog((p) => ({ ...p, guidelines: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCycleDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#2e7d32' }} onClick={saveCycle}
            disabled={!cycleDialog?.name || !cycleDialog?.startDate || !cycleDialog?.endDate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── ADD EMPLOYEE DIALOG ── */}
      <Dialog open={addEmployeeDialog} onClose={() => setAddEmployeeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Add Employee to Cycle
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setAddEmployeeDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select value={addEmpForm.employeeId} label="Employee"
                onChange={(e) => setAddEmpForm((p) => ({ ...p, employeeId: e.target.value }))}>
                {employees.map((e: any) => (
                  <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName} {e.department?.name ? `— ${e.department.name}` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Current Salary" type="number" fullWidth
              value={addEmpForm.currentSalary}
              InputProps={{ startAdornment: <InputAdornment position="start">HKD</InputAdornment> }}
              onChange={(e) => setAddEmpForm((p) => ({ ...p, currentSalary: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEmployeeDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={addEmployee} disabled={!addEmpForm.employeeId}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* ── RECOMMEND DIALOG ── */}
      <Dialog open={!!reviewDialog} onClose={() => setReviewDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Submit Recommendation — {reviewDialog?.employee ? `${reviewDialog.employee.firstName} ${reviewDialog.employee.lastName}` : ''}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setReviewDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">Current salary</Typography>
              <Typography fontWeight={700}>{fmtCurrency(reviewDialog?.currentSalary, reviewDialog?.currency)}</Typography>
            </Box>
            <TextField label="Recommended Salary" type="number" fullWidth required
              value={reviewDialog?.recommendedSalary ?? ''}
              InputProps={{ startAdornment: <InputAdornment position="start">{reviewDialog?.currency}</InputAdornment> }}
              onChange={(e) => setReviewDialog((p) => p ? { ...p, recommendedSalary: Number(e.target.value) } : null)} />
            <TextField label="One-off Bonus (optional)" type="number" fullWidth
              value={reviewDialog?.bonusAmount ?? ''}
              InputProps={{ startAdornment: <InputAdornment position="start">{reviewDialog?.currency}</InputAdornment> }}
              onChange={(e) => setReviewDialog((p) => p ? { ...p, bonusAmount: Number(e.target.value) } : null)} />
            {reviewDialog?.currentSalary && reviewDialog?.recommendedSalary && (
              <Alert severity={Number(reviewDialog.recommendedSalary) > Number(reviewDialog.currentSalary) ? 'success' : 'warning'}>
                {fmtPct((Number(reviewDialog.recommendedSalary) - Number(reviewDialog.currentSalary)) / Number(reviewDialog.currentSalary) * 100)} change
                ({fmtCurrency(Number(reviewDialog.recommendedSalary) - Number(reviewDialog.currentSalary), reviewDialog.currency)} /yr)
              </Alert>
            )}
            <TextField label="Justification" fullWidth multiline rows={3}
              value={reviewDialog?.managerJustification ?? ''}
              onChange={(e) => setReviewDialog((p) => p ? { ...p, managerJustification: e.target.value } : null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#2e7d32' }} onClick={submitRecommendation}
            disabled={!reviewDialog?.recommendedSalary}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── APPROVE DIALOG ── */}
      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Approve Review — {approveDialog?.employee ? `${approveDialog.employee.firstName} ${approveDialog.employee.lastName}` : ''}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setApproveDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Current</Typography>
                <Typography fontWeight={700}>{fmtCurrency(approveDialog?.currentSalary, approveDialog?.currency)}</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Manager rec.</Typography>
                <Typography fontWeight={700} color="success.main">{fmtCurrency(approveDialog?.recommendedSalary, approveDialog?.currency)}</Typography>
              </Box>
            </Box>
            <TextField label="Approved Salary" type="number" fullWidth
              value={approveDialog?.approvedSalary ?? ''}
              InputProps={{ startAdornment: <InputAdornment position="start">{approveDialog?.currency}</InputAdornment> }}
              onChange={(e) => setApproveDialog((p) => p ? { ...p, approvedSalary: Number(e.target.value) } : null)} />
            <TextField label="Approved Bonus" type="number" fullWidth
              value={approveDialog?.approvedBonus ?? ''}
              InputProps={{ startAdornment: <InputAdornment position="start">{approveDialog?.currency}</InputAdornment> }}
              onChange={(e) => setApproveDialog((p) => p ? { ...p, approvedBonus: Number(e.target.value) } : null)} />
            <TextField label="HR Notes (optional)" fullWidth multiline rows={2}
              value={approveDialog?.hrNotes ?? ''}
              onChange={(e) => setApproveDialog((p) => p ? { ...p, hrNotes: e.target.value } : null)} />
            {approveDialog?.managerJustification && (
              <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Manager's justification</Typography>
                <Typography variant="body2">{approveDialog.managerJustification}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={approveReview}>Approve</Button>
        </DialogActions>
      </Dialog>

      {/* ── REJECT DIALOG ── */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Reject & Send Back
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setRejectDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField label="Reason / feedback for manager" fullWidth multiline rows={3} autoFocus
            value={rejectDialog?.hrNotes ?? ''}
            onChange={(e) => setRejectDialog((p) => p ? { ...p, hrNotes: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={rejectReview}
            disabled={!rejectDialog?.hrNotes}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
